import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Star } from 'lucide-react';
import SeoHead from './SeoHead';
import ServicePage, { RelatedServices, ServiceChecklist, ServiceCta, ServiceFaq, ServiceHero } from '../services/ServicePageLayout';
import CarImg from '../../assets/hero.png';
import CarInspectedImg from '../../assets/car-inspected.png';

const AbuDhabi = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const lang = i18n.language === 'en' ? 'en' : 'ar';
    const faq = t('abuDhabiPage.faq', { returnObjects: true });
    const process = t('abuDhabiPage.process', { returnObjects: true });
    const testimonials = t('abuDhabiPage.testimonials', { returnObjects: true });
    const ctaMessage = isRTL
        ? 'مرحباً، أرغب في حجز موعد فحص في أبوظبي.'
        : 'Hi, I would like to book an inspection appointment in Abu Dhabi.';

    return (
        <ServicePage isRTL={isRTL}>
            <SeoHead
                path="abu-dhabi-car-inspect"
                title={t('abuDhabiPage.metaTitle')}
                description={t('abuDhabiPage.metaDescription')}
                keywords={t('abuDhabiPage.keywords')}
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: faq.map(({ q, a }) => ({
                        '@type': 'Question',
                        name: q,
                        acceptedAnswer: { '@type': 'Answer', text: a },
                    })),
                }}
            />

            <ServiceHero
                eyebrow={t('abuDhabiPage.eyebrow')}
                title={t('abuDhabiPage.title')}
                intro={t('abuDhabiPage.intro')}
                image={CarImg}
                imageAlt={t('abuDhabiPage.title')}
                ctaLabel={t('abuDhabiPage.ctaLabel')}
                ctaMessage={ctaMessage}
                secondaryLabel={isRTL ? 'موقع المركز' : 'Get directions'}
                secondaryTo={`/${lang}/contact`}
            />

            <ServiceChecklist eyebrow={t('abuDhabiPage.eyebrow')} title={t('abuDhabiPage.servicesTitle')} points={t('abuDhabiPage.services', { returnObjects: true })} />

            <section className="service-section">
                <div className="service-hero" style={{ padding: 0, gridTemplateColumns: '0.85fr 1.15fr', alignItems: 'center' }}>
                    <div className="service-hero-media" style={{ minHeight: 220 }}>
                        <img src={CarInspectedImg} alt={t('abuDhabiPage.benefitsTitle')} />
                    </div>
                    <div>
                        <h2 style={{ margin: '0 0 14px', fontSize: 'clamp(24px,2.4vw,32px)' }}>{t('abuDhabiPage.benefitsTitle')}</h2>
                        <p style={{ color: 'var(--muted)', lineHeight: 1.8, marginBottom: 18 }}>{t('abuDhabiPage.benefitsIntro')}</p>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 10 }}>
                            {t('abuDhabiPage.benefits', { returnObjects: true }).map((point) => (
                                <li key={point} style={{ display: 'flex', gap: 10, fontSize: 14.5, lineHeight: 1.6 }}>
                                    <span className="service-check-icon" style={{ flexShrink: 0 }}>✓</span>{point}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <ServiceChecklist eyebrow={t('abuDhabiPage.eyebrow')} title={t('abuDhabiPage.whyTitle')} points={t('abuDhabiPage.why', { returnObjects: true })} />

            <section className="service-section">
                <div className="service-section-head">
                    <h2>{t('abuDhabiPage.processTitle')}</h2>
                    <p style={{ color: 'var(--muted)', margin: '-14px 0 0' }}>{t('abuDhabiPage.processIntro')}</p>
                </div>
                <ul className="service-steps" style={{ marginTop: 28 }}>
                    {process.map((step, index) => (
                        <li key={step.title}>
                            <span className="service-step-index">0{index + 1}</span>
                            <h3>{step.title}</h3>
                            <p>{step.body}</p>
                        </li>
                    ))}
                </ul>
            </section>

            <section className="service-section">
                <div className="service-section-head">
                    <h2>{t('abuDhabiPage.testimonialsTitle')}</h2>
                </div>
                <div className="service-testimonials">
                    {testimonials.map(({ quote, name }) => (
                        <div className="service-testimonial" key={name}>
                            <div className="service-stars">
                                {[0, 1, 2, 3, 4].map((star) => <Star key={star} size={15} fill="currentColor" />)}
                            </div>
                            <p>&ldquo;{quote}&rdquo;</p>
                            <strong>{name}</strong>
                        </div>
                    ))}
                </div>
            </section>

            <ServiceFaq title={t('abuDhabiPage.faqTitle')} items={faq} />
            <RelatedServices title={t('abuDhabiPage.related.title')} items={t('abuDhabiPage.related.items', { returnObjects: true })} lang={lang} />

            <ServiceCta
                eyebrow={t('abuDhabiPage.eyebrow')}
                title={t('abuDhabiPage.title')}
                body={t('abuDhabiPage.metaDescription')}
                ctaLabel={t('abuDhabiPage.ctaLabel')}
                ctaMessage={ctaMessage}
            />
            <div className="service-section" style={{ borderTop: 'none', paddingTop: 0, textAlign: 'center' }}>
                <a
                    href="https://maps.app.goo.gl/nVnooyaAdpqJkwcn8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="service-button service-button-ghost"
                    style={{ display: 'inline-flex' }}
                >
                    {isRTL ? 'موقع مركز فحص السيارات' : 'Center location on the map'}<ArrowUpRight size={16} />
                </a>
            </div>
        </ServicePage>
    );
};

export default AbuDhabi;
